import { Alert, Button, DatePicker, Divider, Form, Input, InputNumber, message, notification, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import { containerStore, counterpartyStore, productStore } from '@/stores';
import { buildStoreOptions, productOptions, showError } from '@/utils/common';
import { getTranslatedString } from '@/utils/intl';
import { useMassUpdateCashedStores } from '@/utils/store';

import type { SiImportRow } from './setup';

const DATE_FORMAT = 'YYYY-MM-DD';

interface FormValues {
  siNumber?: string | null;
  purchaseContractNumber?: string | null;
  quantityMt?: number | null;
  destination?: string | null;
  consignee?: string | null;
  notifyParties?: string[];
  shippingLine?: string | null;
  serviceContractNumber?: string | null;
  containerType?: string | null;
  packing?: string | null;
  lotNumbers?: string[];
  certificationLevel?: string | null;
  requiredDocuments?: string[];
  freightPayer?: string | null;
  // Operator-supplied approve fields.
  number?: string | null;
  date?: moment.Moment | null;
  exporterId?: number | null;
  productId?: number | null;
  containerId?: number | null;
}

interface SiReviewDrawerProps {
  importRow: SiImportRow | null;
  onClose: () => void;
  onSubmitted: () => void;
  /** Trigger a list refetch on the page so `importRow` reflects the latest
   *  server state — called after a failed approve so the error banner
   *  re-renders without closing and reopening the drawer. */
  onRefresh: () => Promise<void> | void;
}

const uncertainProps = (uncertain: Set<string>, field: string) =>
  uncertain.has(field)
    ? {
        validateStatus: 'warning' as const,
        help: getTranslatedString('boardings.imports.review.uncertain_field_tooltip'),
      }
    : {};

const toFormValues = (parsed: cyrian.api.documentImports.ShippingInstructionParsed | null | undefined): FormValues => {
  if (!parsed) return {};
  return {
    siNumber: parsed.siNumber ?? null,
    purchaseContractNumber: parsed.purchaseContractNumber ?? null,
    quantityMt: parsed.quantityMt ?? null,
    destination: parsed.destination ?? null,
    consignee: parsed.consignee ?? null,
    notifyParties: parsed.notifyParties ?? [],
    shippingLine: parsed.shippingLine ?? null,
    serviceContractNumber: parsed.serviceContractNumber ?? null,
    containerType: parsed.containerType ?? null,
    packing: parsed.packing ?? null,
    lotNumbers: parsed.lotNumbers ?? [],
    certificationLevel: parsed.certificationLevel ?? null,
    requiredDocuments: parsed.requiredDocuments ?? [],
    freightPayer: parsed.freightPayer ?? null,
    // A previously failed approve keeps the operator inputs in parsed_data —
    // pre-fill them so the second round doesn't start from scratch.
    number: parsed.number ?? null,
    date: parsed.date ? moment(parsed.date) : null,
    exporterId: parsed.exporterId ?? null,
    productId: parsed.productId ?? null,
    containerId: parsed.containerId ?? null,
  };
};

const toApiPayload = (values: FormValues): cyrian.api.documentImports.ShippingInstructionParsed => ({
  siNumber: values.siNumber || null,
  purchaseContractNumber: values.purchaseContractNumber || null,
  quantityMt: typeof values.quantityMt === 'number' ? values.quantityMt : null,
  destination: values.destination || null,
  consignee: values.consignee || null,
  notifyParties: values.notifyParties ?? [],
  shippingLine: values.shippingLine || null,
  serviceContractNumber: values.serviceContractNumber || null,
  containerType: values.containerType || null,
  packing: values.packing || null,
  lotNumbers: values.lotNumbers ?? [],
  certificationLevel: values.certificationLevel || null,
  requiredDocuments: values.requiredDocuments ?? [],
  freightPayer: values.freightPayer || null,
  number: values.number || null,
  date: values.date ? values.date.format(DATE_FORMAT) : null,
  exporterId: values.exporterId ?? null,
  productId: values.productId ?? null,
  containerId: values.containerId ?? null,
});

// Highlight fields the backend resolvers blamed after a failed approve.
// Patterns are anchored on the phrasings of
// `Cyrian-Backend/src/common/utils/shipping-instruction-resolvers.util.ts`
// and the required-field checks in `BoardingService.upsertFromSi` (regex,
// not exact match, to tolerate minor wording tweaks).
const inferFailingFields = (error: string | null | undefined): string[] => {
  if (!error) return [];
  const fields: string[] = [];
  if (/purchase contract\b[^.]*(not found|ambiguous)/i.test(error)) fields.push('purchaseContractNumber');
  if (/service contract\b[^.]*(not found|ambiguous)/i.test(error)) fields.push('serviceContractNumber');
  if (/consignee\b[^.]*not found/i.test(error)) fields.push('consignee');
  if (/freight payer\b[^.]*not found/i.test(error)) fields.push('freightPayer');
  if (/harbor\b[^.]*(not found|ambiguous)/i.test(error) || /destination is required/i.test(error)) {
    fields.push('destination');
  }
  if (/container\b[^.]*(not found|is required)/i.test(error)) fields.push('containerType');
  if (/lot\(s\)|lot list/i.test(error)) fields.push('lotNumbers');
  if (/si number is missing/i.test(error)) fields.push('siNumber');
  if (/boarding date\b/i.test(error)) fields.push('date');
  if (/exporter\b/i.test(error)) fields.push('exporterId');
  if (/product\b[^.]*(is required|not found)/i.test(error)) fields.push('productId');
  return fields;
};

// Matches the backend `normalizeForMatch` (NFD decompose → strip combining
// diacritics → lowercase → trim) so the inline-create duplicate pre-check
// agrees with the resolver: "San-Pédro" typed as "SAN-PEDRO" must show
// "already exists", not double-create.
const normalizeForMatch = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

// After the operator resolves the destination via inline Create, strip the
// harbor sentence(s) from the error banner so only outstanding problems stay
// visible. The backend joins resolver messages with single spaces between
// sentences ending in '.'.
const filterErrorByResolved = (error: string | null | undefined, resolved: Set<string>): string => {
  if (!error || resolved.size === 0) return error ?? '';
  const sentences = error.split(/(?<=\.)\s+/);
  const kept = sentences.filter((s) => !(resolved.has('destination') && /harbor|destination/i.test(s)));
  return kept.join(' ').trim();
};

// Candidate cap for the harbor duplicate pre-check. The check must be
// accent-insensitive (backend `name` search is ILIKE — case-insensitive but
// accent-SENSITIVE, so "San-Pedro" would miss an existing "San-Pédro"), and
// the backend has no normalized search — so every existing harbor must be
// compared client-side. Harbors are a small dictionary (dozens); if it ever
// outgrows this page, the check refuses loudly instead of silently waving a
// duplicate through (see the `count` guard in onCreateHarbor).
const HARBOR_DUP_CHECK_LIMIT = 200;

// Free-text chips input: values come from the document or the operator's
// keyboard; there are no suggestions to offer, so hide the empty dropdown.
const TAGS_SELECT_PROPS = {
  mode: 'tags' as const,
  tokenSeparators: [','],
  allowClear: true,
  notFoundContent: null,
};

export const SiReviewDrawer = observer(({ importRow, onClose, onSubmitted, onRefresh }: SiReviewDrawerProps) => {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [creatingHarbor, setCreatingHarbor] = useState(false);
  // Fields the operator just resolved client-side via inline Create — drop
  // out of highlights/banner until the next approve refreshes `row.error`.
  const [resolvedFields, setResolvedFields] = useState<Set<string>>(new Set());

  // Approve-time pickers (exporter/product/container) need master data; the
  // stores are cached (10 s TTL), so this is cheap on repeated opens.
  useMassUpdateCashedStores([counterpartyStore, productStore, containerStore]);

  // Reset form only when a *different* row is opened — a same-row refresh
  // (e.g. after a failed approve updates `importRow.error`) must not wipe
  // the operator's in-progress edits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate — depend on the row id only, see comment above
  useEffect(() => {
    if (importRow) {
      form.resetFields();
      form.setFieldsValue(toFormValues(importRow.parsedData));
      setResolvedFields(new Set());
    }
  }, [importRow?.id, form]);

  const uncertain = useMemo<Set<string>>(() => {
    const set = new Set<string>(importRow?.parsedData?._uncertainFields ?? []);
    if (importRow?.status === 'review') {
      for (const field of inferFailingFields(importRow.error)) set.add(field);
    }
    for (const field of resolvedFields) set.delete(field);
    return set;
  }, [importRow, resolvedFields]);

  const onSubmit = async (values: FormValues) => {
    if (!importRow) return;
    setSubmitting(true);
    try {
      // Retry responds 200 even when persistence re-parks the row in
      // `review` (resolver misses are recorded on the row, not thrown up
      // the wire) — so the outcome is read from the returned row's status,
      // not from the HTTP status.
      const res = await api.documentImports.retry(importRow.id, { parsedData: toApiPayload(values) }).source;
      const updated = res?.data;
      if (updated?.status === 'review') {
        message.warning(getTranslatedString('boardings.imports.review.submit_parked'));
        setResolvedFields(new Set());
        // Refresh so `importRow.error` carries the new resolver misses —
        // the banner and field highlights re-render; the drawer stays open
        // with the operator's edits intact (same row id → no form reset).
        await onRefresh();
        return;
      }
      if (updated?.status === 'failed') {
        // Non-actionable failure — retry from `failed` is not allowed, so
        // keeping the drawer open would only show a dead form.
        notification.error({ message: updated.error ?? getTranslatedString('boardings.imports.review.submit_failed') });
        onSubmitted();
        onClose();
        return;
      }
      if (updated?.status !== 'persisted') {
        // Defensive: retry always returns a terminal status today, but a
        // 200 with an unexpected/absent body must not read as "boarding
        // created" — keep the drawer open and refresh instead.
        notification.error({ message: getTranslatedString('boardings.imports.review.submit_failed') });
        await onRefresh();
        return;
      }
      message.success(getTranslatedString('boardings.imports.review.submit_success'));
      onSubmitted();
      onClose();
    } catch (err) {
      // Genuine HTTP errors only (network, wrong-state 400 after a
      // concurrent change) — resolver misses come back as 200/review.
      showError(err);
      await onRefresh();
    } finally {
      setSubmitting(false);
    }
  };

  // Inline harbor create for the destination port — same pattern (and the
  // same duplicate pre-check rationale) as the waybill ReviewDrawer.
  const onCreateHarbor = async () => {
    const destination = form.getFieldValue('destination') as string | null | undefined;
    if (!destination) {
      message.error(getTranslatedString('boardings.imports.review.harbor.create_missing'));
      return;
    }
    setCreatingHarbor(true);
    const key = normalizeForMatch(destination);
    try {
      // No server-side `name` filter on purpose — see HARBOR_DUP_CHECK_LIMIT.
      const existing = await api.addresses.getAll({ type: 'Harbor', limit: HARBOR_DUP_CHECK_LIMIT }).source;
      const items: cyrian.api.addresses.Address[] = existing?.data?.items ?? [];
      const count: number = existing?.data?.count ?? items.length;
      // The pre-check is only sound when every existing harbor was inspected.
      if (count > items.length) {
        message.error(getTranslatedString('boardings.imports.review.harbor.dup_check_overflow'));
        return;
      }
      const dup = items.find((a) => normalizeForMatch(a.name) === key);
      if (dup) {
        setResolvedFields((prev) => new Set(prev).add('destination'));
        message.warning(getTranslatedString('boardings.imports.review.harbor.already_exists', { name: destination }));
        return;
      }
      await api.addresses.create({
        name: destination,
        type: 'Harbor',
        country: '',
        city: '',
        street: '',
        street2: '',
      }).source;
      setResolvedFields((prev) => new Set(prev).add('destination'));
      message.success(getTranslatedString('boardings.imports.review.harbor.create_success', { name: destination }));
    } catch (err) {
      showError(err);
    } finally {
      setCreatingHarbor(false);
    }
  };

  // Banner precedence: a failed approve (row.error) is fresher and more
  // actionable than the OCR self-flags, so it wins.
  const visibleError = filterErrorByResolved(importRow?.error, resolvedFields);
  const ocrUncertainOutstanding = (importRow?.parsedData?._uncertainFields ?? []).some((f) => !resolvedFields.has(f));
  const issueBanner = visibleError
    ? { key: 'boardings.imports.review.banner.entity_not_found', description: visibleError }
    : ocrUncertainOutstanding
      ? {
          key: 'boardings.imports.review.banner.low_confidence',
          description: importRow?.parsedData?._extractionNotes || undefined,
        }
      : null;

  const requiredRule = { required: true, message: getTranslatedString('boardings.imports.review.required') };

  return (
    <Drawer
      visible={Boolean(importRow)}
      onClose={onClose}
      title={getTranslatedString('boardings.imports.review.title')}
      width={640}
      footer={(close) => (
        <div className="drawer-footer">
          <Button className="drawer-footer-button" onClick={close}>
            <FormattedMessage id="boardings.imports.review.cancel" />
          </Button>
          <Button type="primary" loading={submitting} onClick={() => form.submit()}>
            <FormattedMessage id="boardings.imports.review.submit" />
          </Button>
        </div>
      )}
    >
      {importRow && (
        <>
          {/* Amendment vs create — always visible so the operator knows what
              approving does before touching anything. */}
          {importRow.amendmentOfBoardingId != null ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <FormattedMessage
                  id="boardings.imports.review.banner.amendment"
                  values={{ id: importRow.amendmentOfBoardingId }}
                />
              }
            />
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={<FormattedMessage id="boardings.imports.review.banner.create" />}
            />
          )}
          {issueBanner && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={<FormattedMessage id={issueBanner.key} />}
              description={issueBanner.description}
            />
          )}
          <Form<FormValues> form={form} layout="vertical" onFinish={onSubmit}>
            <Divider orientation="left" plain>
              <FormattedMessage id="boardings.imports.review.section.extracted" />
            </Divider>
            <Form.Item
              name="siNumber"
              label={<FormattedMessage id="boardings.imports.review.siNumber" />}
              rules={[requiredRule]}
              {...uncertainProps(uncertain, 'siNumber')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="quantityMt"
              label={<FormattedMessage id="boardings.imports.review.quantityMt" />}
              {...uncertainProps(uncertain, 'quantityMt')}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="purchaseContractNumber"
              label={<FormattedMessage id="boardings.imports.review.purchaseContractNumber" />}
              {...uncertainProps(uncertain, 'purchaseContractNumber')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="serviceContractNumber"
              label={<FormattedMessage id="boardings.imports.review.serviceContractNumber" />}
              {...uncertainProps(uncertain, 'serviceContractNumber')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="destination"
              label={<FormattedMessage id="boardings.imports.review.destination" />}
              {...uncertainProps(uncertain, 'destination')}
            >
              <Input
                addonAfter={
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    loading={creatingHarbor}
                    onClick={() => void onCreateHarbor()}
                  >
                    <FormattedMessage id="boardings.imports.review.harbor.create_inline" />
                  </Button>
                }
              />
            </Form.Item>
            <Form.Item
              name="consignee"
              label={<FormattedMessage id="boardings.imports.review.consignee" />}
              {...uncertainProps(uncertain, 'consignee')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="freightPayer"
              label={<FormattedMessage id="boardings.imports.review.freightPayer" />}
              {...uncertainProps(uncertain, 'freightPayer')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="notifyParties"
              label={<FormattedMessage id="boardings.imports.review.notifyParties" />}
              {...uncertainProps(uncertain, 'notifyParties')}
            >
              <Select {...TAGS_SELECT_PROPS} />
            </Form.Item>
            <Form.Item
              name="shippingLine"
              label={<FormattedMessage id="boardings.imports.review.shippingLine" />}
              {...uncertainProps(uncertain, 'shippingLine')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="containerType"
              label={<FormattedMessage id="boardings.imports.review.containerType" />}
              {...uncertainProps(uncertain, 'containerType')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="packing"
              label={<FormattedMessage id="boardings.imports.review.packing" />}
              {...uncertainProps(uncertain, 'packing')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="certificationLevel"
              label={<FormattedMessage id="boardings.imports.review.certificationLevel" />}
              {...uncertainProps(uncertain, 'certificationLevel')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="lotNumbers"
              label={<FormattedMessage id="boardings.imports.review.lotNumbers" />}
              {...uncertainProps(uncertain, 'lotNumbers')}
            >
              <Select {...TAGS_SELECT_PROPS} />
            </Form.Item>
            <Form.Item
              name="requiredDocuments"
              label={<FormattedMessage id="boardings.imports.review.requiredDocuments" />}
              {...uncertainProps(uncertain, 'requiredDocuments')}
            >
              <Select {...TAGS_SELECT_PROPS} />
            </Form.Item>

            <Divider orientation="left" plain>
              <FormattedMessage id="boardings.imports.review.section.approval" />
            </Divider>
            <Form.Item
              name="date"
              label={<FormattedMessage id="boardings.imports.review.date" />}
              rules={[requiredRule]}
              {...uncertainProps(uncertain, 'date')}
            >
              <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
            </Form.Item>
            <Form.Item
              name="exporterId"
              label={<FormattedMessage id="boardings.imports.review.exporter" />}
              rules={[requiredRule]}
              {...uncertainProps(uncertain, 'exporterId')}
            >
              <Select showSearch optionFilterProp="label" options={buildStoreOptions(counterpartyStore, 'name')} />
            </Form.Item>
            <Form.Item name="number" label={<FormattedMessage id="boardings.imports.review.number" />}>
              <Input placeholder={getTranslatedString('boardings.imports.review.number-placeholder')} />
            </Form.Item>
            <Form.Item
              name="productId"
              label={<FormattedMessage id="boardings.imports.review.product" />}
              {...uncertainProps(uncertain, 'productId')}
            >
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                options={productOptions()}
                placeholder={getTranslatedString('boardings.imports.review.product-placeholder')}
              />
            </Form.Item>
            <Form.Item name="containerId" label={<FormattedMessage id="boardings.imports.review.container" />}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                options={buildStoreOptions(containerStore, 'name')}
                placeholder={getTranslatedString('boardings.imports.review.container-placeholder')}
              />
            </Form.Item>
          </Form>
        </>
      )}
    </Drawer>
  );
});
